/**
 * Subscription Review Step
 *
 * Second step in subscription purchase flow - payment method collection.
 * Similar to booking's PaymentDetailsStep but focused on subscriptions.
 *
 * **Flow:**
 * 1. Email Entry → User enters email, clicks "Save Payment Method"
 * 2. Check User → Call /users/guest to check if user exists
 * 3a. Existing User → Show sign-in modal
 * 3b. New User → Show payment form
 * 4. Payment Saved → Auto-advance to FinishAccountStep
 *
 * **Features:**
 * - Subscription details card with features list
 * - Billing details (start date, cycle, next billing)
 * - Price summary with tax calculation
 * - Email + payment flow (reuses booking components)
 * - Automatic user authentication check
 *
 * @module pages/subscription/steps
 */

import { needsAccountSetup } from '@butter/library/shared';
import { useMemo, useState, useEffect } from 'react';
import {
  useSubscriptionPurchase,
  useSubscriptionStep,
  useCreateUserMembership,
  useCreateUserPass,
  useActiveSubscription,
  type PaymentProvider,
} from '~/services/internalApi/subscription';
import { getCreditBalanceChange, getPromotionRejection } from '~/lib/apiClient';
import { isHoldingRefusalCode } from '@butter/library/subscription';
import type { ApiErrorResult } from '~/lib/apiClient/types';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useHomeClub } from '~/services/internalApi/club';
import { useAuth } from '~/services/auth';
import { usePayment } from '~/features/payment';
import { usePaymentMethodsQuery, reportPaymentClientError, useCardSave, useAutoAdvanceAfterSave, CardEntryForm } from '~/features/payment';
import { useTenantHandle } from '~/services/appRuntime';
import { detectPaymentProvider, getPaymentSettings } from '~/lib/utils/paymentProvider';
import { toNumericUserId } from '~/lib/utils/userIdUtils';
import { addBreadcrumb, setUser } from '@butter/monitoring';
import { PriceSummaryCard, PaymentMethodSelector, UNSELECTABLE_METHOD_TYPES } from '@butter/ui/domains/billing';
import { MembershipCardGradient, KeyBenefitsList, deriveKeyBenefits } from '@butter/ui/domains/subscriptions';
import { BookingDetailsCard } from '@butter/ui/domains/booking';
import { calcBillingDetails } from '@butter/ui/utils';

import { EmailPaymentCard } from '../../booking/components/EmailPaymentCard';
import { SignInModal } from '~/ui/layout/SignInModal';
import { userService } from '~/services/user';
import { butterToast } from '@butter/ui/patterns';
import { ArrowIcon, CheckmarkIcon } from '@butter/ui/icons';
import { Checkbox } from '@butter/ui/primitives/inputs';
import { QueryParams } from '~/lib/queryParams';
import { useTenantNavigate, useTenantSearchParams } from '~/lib/navigation';
import { useSubscriptionPurchasePricing } from './useSubscriptionPurchasePricing';
import { QuoteFailedBanner, QuoteRefusedPanel, PromoNotice } from './QuoteBanners';
import { PromoCodeField } from './PromoCodeField';
import { QuantityStepper } from './QuantityStepper';
import { GiftRecipientsField } from './GiftRecipientsField';
import {
  DEFAULT_GIFTING_MESSAGE,
  GIFT_ALL_MESSAGE,
  areRecipientsComplete,
  giftOrderFields,
  passReceiptCompositeId,
  promoGiftingNote,
  recipientsNeeded,
  resolveGiftAll,
  refusedRecipientIndex,
} from './giftRecipients';
import { MAX_PASSES_PER_ORDER } from '@lgc/database/types/domains/subscription';
import { useWaiverGateHold } from "../../../features/waivers/waiverGateHold";


/**
 * Payment flow steps
 */
type PaymentStep =
  | 'email'           // Email entry + "Save Payment Method" button
  | 'sign-in'         // Existing user authentication
  | 'payment';        // Payment method capture

/**
 * Subscription Review Step Component
 *
 * Manages the payment flow with multiple sub-steps:
 * - Email entry
 * - User authentication (if existing)
 * - Payment method capture
 *
 * Auto-advances to FinishAccountStep after payment is saved.
 */
export function SubscriptionReviewStep() {
  // Account completion flips auth mid-step; hold the waiver entry gate so it
  // can't swap the purchase flow for a SignWaiversScreen before the purchase
  // lands (see features/waivers/waiverGateHold.ts). The subscription flow
  // captures no waiver by design — the member meets the gate on their next
  // gated flow, and the receipt route renders regardless.
  useWaiverGateHold();

  const navigate = useTenantNavigate();
  const [, setParams] = useTenantSearchParams();
  const [tenantHandle] = useTenantHandle();
  const {
    selectedSubscription,
        guestUser,
    setGuestUser,
    setPaymentMethodId,
    paymentMethodId,
    clearPurchase,
    promoCode,
    clearPromoCode,
    quantity,
    giftAll,
    recipients,
    setQuantity,
    setGiftAll,
    setRecipient,
  } = useSubscriptionPurchase();

  const { goToPreviousStep, goToNextStep } = useSubscriptionStep();
  const { data: club } = useClubQuery();
  // Payment methods are always created/charged at the user's home club.
  // Authenticated users must initialize the payment form with the home club's
  // credentials even when visiting another club's portal.
  const { homeClub } = useHomeClub();
  const { state, setEmail } = usePayment();
  const { isAuthenticated, user: authUser } = useAuth();

  // Local state for payment flow UI
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('email');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSaved, setPaymentSaved] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the current `error` is the promo auto-drop (an EXPECTED refusal).
  // Drives the banner's `data-kind` — the e2e hook, so specs assert the
  // classification, never the composed copy.
  const [promoDropped, setPromoDropped] = useState(false);
  // The holding refusal the server answered with, when it did — the banner's
  // `data-kind`, so a spec can tell an expected refusal from a fault.
  const [refusalKind, setRefusalKind] = useState<string | null>(null);
  // Which recipient the PURCHASE refused (the saga's answer) — captured
  // from the failure handler, cleared when that recipient is edited.
  const [refusedRecipient, setRefusedRecipient] = useState<number | null>(null);

  // Fetch saved payment methods for authenticated users
  const { data: savedPaymentMethods = [] } = usePaymentMethodsQuery({
    enabled: isAuthenticated,
  });

  /**
   * The one failure path for both create mutations (envelope shape): report,
   * auto-drop a refused promo, surface the copy. `promotion_rejected` is the
   * expected business failure — it arrives on the ENVELOPE, never a throw.
   */
  const handleCreateFailure = (
    kind: 'create_membership' | 'create_pass',
    result: ApiErrorResult,
    fallback: string,
  ) => {
    console.error(`[SubscriptionReviewStep] ${kind} failed:`, result.message);
    // The buyer's credit no longer covers the purchase (spent since the quote). Nothing was
    // bought or charged: re-quote so the card step returns (credits D30).
    const creditChange = getCreditBalanceChange(result);
    if (creditChange) {
      void quoteQuery.refetch();
      setError(creditChange);
      setIsProcessing(false);
      return;
    }
    const rejection = getPromotionRejection(result);
    const code = result.error?.code;
    // The server said no for a reason the product owns: a promo that died
    // between quote and Pay, a holding the pre-gate could not see (a queued
    // membership is hidden from the member route — subscription unification
    // D11.3), an ineligible gift recipient. Expected refusals are still
    // captured (how often they happen is a signal) but at WARNING, never a
    // paging error (re-review N-4; D12).
    const expectedRefusal = !!rejection || isHoldingRefusalCode(code) || code === 'gift_recipient_ineligible';
    setRefusalKind(isHoldingRefusalCode(code) ? code : null);
    // A constructed Error, never the raw envelope — captureException on a
    // plain object titles the event "[object Object]". The envelope's
    // classification rides `extra` instead (richer than the thrown-Error era).
    reportPaymentClientError(kind, new Error(result.message || fallback), {
      kind: 'mutation',
      surface: 'web_subscription',
      ...(expectedRefusal && { level: 'warning' }),
      extra: { code, httpStatus: result.httpStatus },
    });
    if (result.error?.code === 'gift_recipient_ineligible') {
      // The order was refused BEFORE any charge. Point at the recipient and keep
      // everything else — the code, the quantity, the other recipients.
      const details = result.error.details as { index?: unknown } | null | undefined;
      setRefusedRecipient(
        typeof details?.index === 'number' ? details.index : refusedRecipientIndex(result.error.field),
      );
      setPromoDropped(false);
      setError(result.message || fallback);
    } else if (rejection?.quantityFixable) {
      // The code is fine; the ORDER is the wrong size for it. Keep
      // the code — dropping it would take away a discount one click from applying.
      setPromoDropped(false);
      setError(rejection.message);
    } else if (rejection) {
      // Auto-drop (7a): forget the code — the key change requotes at list
      // price — and tell the member why in the server's words.
      clearPromoCode();
      setPromoDropped(true);
      setError(
        `${rejection.message} The code was removed — you can complete your purchase at the regular price.`,
      );
    } else {
      setPromoDropped(false);
      setError(result.message || fallback);
    }
    setIsProcessing(false);
  };

  /** Genuine exceptions only — apiClient never throws, so this is a programmer error. */
  const handleCreateException = (kind: string, error: Error, fallback: string) => {
    console.error(`[SubscriptionReviewStep] ${kind} threw:`, error);
    reportPaymentClientError(kind, error, { kind: 'mutation', surface: 'web_subscription' });
    setRefusalKind(null);
    setError(error.message || fallback);
    setIsProcessing(false);
  };

  // Mutation hooks for creating memberships and passes (for authenticated users)
  const createMembership = useCreateUserMembership({
    onSuccess: (data) => {
      console.log('[SubscriptionReviewStep] Membership created:', data.userMembership.id);

      addBreadcrumb(
        'subscription',
        'Membership purchase completed successfully',
        {
          userMembershipId: data.userMembership.id,
          membershipId: data.userMembership.membershipId,
          userId: data.userMembership.userId,
        },
        'info'
      );

      // Clear payment data from context
      clearPurchase();

      // Navigate to confirmation page with data in state for immediate rendering
      navigate(`/subscriptions/confirmed/membership-${data.userMembership.id}`, {
        state: {
          subscription: data,
          type: 'membership',
          pricing: {
            subtotal,
            tax,
            total,
            taxRate,
            // The receipt's Setup Fee row reads this — omitting it here (and
            // only here) was why authenticated buyers never saw the row.
            setupFee,
            // Receipt Discount row (7a+m) — present only when a promo priced in.
            ...(discountTotal > 0 && { discountTotal, promoLabel: promotion?.label }),
          },
        }
      });
      // AFTER navigate: clearing changes the quote key and would fire a
      // throwaway list-price refetch on a page that is about to unmount.
      clearPromoCode();
    },
    onFailure: (result) => handleCreateFailure('create_membership', result, 'Failed to create membership'),
    onError: (error) => handleCreateException('create_membership', error, 'Failed to create membership'),
  });

  const createPass = useCreateUserPass({
    onSuccess: (data) => {
      // A PURE GIFT returns no `userPass` — the buyer kept none,
      // so every field below is read off whichever record they actually own.
      const receipt = passReceiptCompositeId(data);
      if (!receipt) {
        // The server returned neither a pass nor an invoice. Unreachable through
        // the public API — a pure gift needs a paid pass, and a kept-one order
        // always has the buyer's row — but the money has already moved, so this
        // must not fail silently. Deliberately NOT redirected: `/subscriptions`
        // is the purchase funnel, and dropping a charged buyer back at step 0
        // is worse than the invalid-link panel they get below.
        reportPaymentClientError('subscriptionreviewstep_receipt', new Error('Purchase returned neither a userPass nor an invoice'), {
          kind: 'response_anomaly',
          surface: 'web_subscription',
          extra: { giftedCount: data.giftedPasses?.length ?? 0 },
        });
      }
      console.log('[SubscriptionReviewStep] Pass created:', receipt);

      addBreadcrumb(
        'subscription',
        'Pass purchase completed successfully',
        {
          userPassId: data.userPass?.id ?? null,
          passId: data.userPass?.passId ?? data.pass?.id ?? null,
          userId: data.userPass?.userId ?? null,
          giftedCount: data.giftedPasses?.length ?? 0,
        },
        'info'
      );

      // Clear payment data from context
      clearPurchase();

      // Navigate to confirmation page with data in state for immediate rendering
      navigate(`/subscriptions/confirmed/${receipt}`, {
        state: {
          subscription: data,
          type: data.userPass ? 'pass' : 'gift',
          pricing: {
            subtotal,
            tax,
            total,
            taxRate,
            // The receipt's Setup Fee row reads this — omitting it here (and
            // only here) was why authenticated buyers never saw the row.
            setupFee,
            // Receipt Discount row (7a) — present only when a promo priced in.
            ...(discountTotal > 0 && { discountTotal, promoLabel: promotion?.label }),
          },
        }
      });
      // The code is spent — a stale capture must not ride into the member's
      // NEXT purchase. AFTER navigate: clearing changes the quote key and
      // would fire a throwaway list-price refetch pre-unmount.
      clearPromoCode();
    },
    onFailure: (result) => handleCreateFailure('create_pass', result, 'Failed to create pass'),
    onError: (error) => handleCreateException('create_pass', error, 'Failed to create pass'),
  });


  // Resolve the payment settings the form should be initialized with.
  // Authenticated users → home club (tokens must target the home club's provider).
  // Guests have no home club yet, so fall back to the visited club.
  const effectivePaymentSettings = useMemo(
    () => getPaymentSettings(isAuthenticated ? homeClub : club),
    [isAuthenticated, homeClub, club]
  );

  // Detect payment provider from the effective (home or current) club settings
  const paymentProvider = useMemo(
    () => detectPaymentProvider(effectivePaymentSettings),
    [effectivePaymentSettings]
  );

  // The user (or guest) the card is saved for — needed by the Stripe SetupIntent.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cardUserIdRaw = authUser?.id ?? (guestUser as any)?.id;
  const cardUserId = useMemo(
    () => (cardUserIdRaw != null ? toNumericUserId(cardUserIdRaw) : undefined),
    [cardUserIdRaw]
  );

  // The new-card form is shown whenever we're NOT showing the saved-methods list.
  const showingNewPaymentForm = !(
    isAuthenticated && savedPaymentMethods.length > 0 && !showNewPaymentForm
  );

  // Stripe SetupIntent wiring + the shared confirm/save primitive. The SetupIntent
  // is fetched only while the Stripe card form is actually visible.
  const {
    stripeSetupIntent,
    stripeSetupIntentLoading,
    stripeSetupIntentError,
    retryStripeSetupIntent,
    saveCard,
  } = useCardSave({
    userId: cardUserId,
    clubId: club?.id,
    tenantHandle: tenantHandle || undefined,
    paymentProvider,
    stripeSetupEnabled: paymentProvider === 'stripe' && showingNewPaymentForm,
  });

  // Server-authored price (D12) — the client renders the quote, it never
  // derives. One hook serves this step and FinishAccountStep (same wiring,
  // same query key — React Query dedupes), so the two steps cannot disagree.
  const {
    query: quoteQuery,
    refused: quoteRefused,
    quoteUnavailable,
    priceUnknown,
    subtotal,
    tax,
    total,
    taxRate,
    processingFee,
    processingFeeRate,
    setupFee,
    lineItems: displayLineItems,
    discountTotal,
    promotion,
    noCardNeeded: quoteNeedsNoCard,
    creditCovered,
    credit,
  } = useSubscriptionPurchasePricing(
    selectedSubscription,
    club?.id,
    promoCode,
    quantity,
    // Quote as the buyer once we know who they are: a per-customer cap or a
    // new-customers rule is theirs, not the plan's, and finding out here beats
    // finding out after the card.
    isAuthenticated,
  );
  // Only a signed-in buyer skips the card: a guest saves one to create their account.
  const noCardNeeded = isAuthenticated && quoteNeedsNoCard;
  // The card is charged what is due today, not the total, when credit pays part of it.
  const payNowLabel = (credit && credit.appliedCents > 0 ? credit.dueNowCents / 100 : total).toFixed(2);

  // Only a code the quote actually priced rides the purchase — never one the
  // quote already refused (the server would refuse it again, less gracefully).
  const promoCodeForPurchase = promotion?.applied ? promotion.code : undefined;

  // ── Gifting — all DERIVED in render; nothing here is copied into state ──
  // A free pass takes no multiples, so it gets no stepper at all.
  const isPass = selectedSubscription?.subscriptionType === 'pass';
  const listPrice = parseFloat(selectedSubscription?.price.replace(/[^\d.]/g, '') ?? '') || 0;
  const showQuantity = isPass && listPrice > 0;
  // The ceiling is the SERVER's: the order cap, or an applied code's own when lower.
  const maxQuantity = Math.min(
    MAX_PASSES_PER_ORDER,
    promotion?.applied && promotion.maxItemsPerRedemption ? promotion.maxItemsPerRedemption : MAX_PASSES_PER_ORDER,
  );

  // A buyer who already holds a plan is steered toward gifting — the purchase
  // saga skips their own holding check precisely when they keep nothing, so
  // gifting is the one shape that works for them. The lock is the server's own
  // rule, read off the same `holdings` cards through the same `canHold`
  // (`resolveGiftAll`): a held pass or membership — live, queued or scheduled —
  // locks the box, because unticking would only buy a refusal at Pay. All
  // derived in render — no effect, nothing copied into state; `giftAll` stays
  // tri-state so a holder who may buy starts unticked rather than defaulted.
  //
  // Plans in `holdings` are tenant-wide, like the server's rule (`findHoldings`):
  // a pass or membership held at a sibling club locks here too, because the
  // server would refuse the kept pass for it.
  const { holdings, isLoading: giftStatusLoading } =
    useActiveSubscription({
      // The signed-in buyer only — a guest holds nothing by construction, and
      // asking on their behalf would 401.
      userId: isAuthenticated ? cardUserId : null,
      enabled: isAuthenticated && showQuantity,
    });
  // Withhold the control until we know, so it cannot flicker from unticked to
  // ticked-and-locked under the buyer. Only LOADING withholds: on an error the
  // flags stay false and the plain checkbox renders, because that query fails
  // routinely (a sibling-club portal answers 403) and a failure sticks for the
  // 5-minute cache. Losing the explanation is acceptable; losing the ability to
  // gift is not, and Pay is never gated on this query at all.
  const giftStatusPending = showQuantity && isAuthenticated && giftStatusLoading;
  // The truth table lives in `resolveGiftAll` so it can be tested without
  // mounting this component.
  const { effective: giftAllEffective, locked: giftLocked, lockedBy: giftLockedBy, lockedByName: giftLockedByName } = showQuantity
    ? resolveGiftAll({ stored: giftAll, holdings })
    : { effective: false, locked: false, lockedBy: null, lockedByName: null };

  const recipientsComplete = !showQuantity || areRecipientsComplete(recipients, quantity, giftAllEffective);
  // A code the order is the wrong SIZE for: offer the one-click fix rather than
  // silently changing what the buyer chose.
  const quantityFix =
    promotion && !promotion.applied
      ? promotion.reason === 'item_limit_exceeded' && promotion.maxItemsPerRedemption
        ? promotion.maxItemsPerRedemption
        : promotion.reason === 'item_minimum_not_met' &&
            promotion.minItemsPerRedemption &&
            promotion.minItemsPerRedemption <= MAX_PASSES_PER_ORDER
          ? promotion.minItemsPerRedemption
          : null
      : null;

  // Admin-authored key benefits when present; otherwise the shared config-derived
  // fallback (the same logic the browse pages and admin preview use).
  const benefits = useMemo(
    () =>
      selectedSubscription
        ? selectedSubscription.keyBenefits?.length
          ? selectedSubscription.keyBenefits
          : deriveKeyBenefits(selectedSubscription)
        : [],
    [selectedSubscription],
  );

  // Calculate billing details
  const billingDetails = useMemo(() => {
    if (!selectedSubscription || !club?.timezone) {
      return { startDate: '', billingCycle: '', nextBillingDate: '' };
    }
    return calcBillingDetails(selectedSubscription, club.timezone);
  }, [selectedSubscription, club]);

  // Auto-advance a new guest to the next step once their card is saved
  // (authenticated users pay via their explicit CTA instead).
  useAutoAdvanceAfterSave({
    paymentSaved,
    setPaymentSaved,
    setIsProcessing,
    guestUser,
    isAuthenticated,
    hasSavedPaymentMethod: !!paymentMethodId,
    goToNextStep,
    surface: 'web_subscription',
  });

  // Auto-advance authenticated users directly to payment step
  useEffect(() => {
    if (isAuthenticated && paymentStep === 'email') {
      console.log('[SubscriptionReviewStep] Authenticated user detected, skipping to payment step');
      setPaymentStep('payment');

      // Set monitoring user context for authenticated users
      if (authUser) {
        setUser({
          id: typeof authUser.id === 'number' ? authUser.id : parseInt(authUser.id?.toString() || '0'),
          email: authUser.email,
          username: `${authUser.firstName} ${authUser.lastName}`.trim(),
        });

        addBreadcrumb(
          'subscription',
          'Authenticated user accessed subscription review step',
          {
            userId: authUser.id,
            subscriptionType: selectedSubscription?.subscriptionType,
            subscriptionId: selectedSubscription?.id,
          },
          'info'
        );
      }
    }
  }, [isAuthenticated, paymentStep, authUser, selectedSubscription]);

  // Auto-select default payment method or first one for authenticated users
  useEffect(() => {
    if (isAuthenticated && savedPaymentMethods.length > 0 && !selectedPaymentMethodId && !showNewPaymentForm) {
      // Find default payment method, or use first one
      // Only consider methods this surface can actually charge. An Optix-migrated
      // member's DEFAULT is often a bank row, so without this filter we'd
      // auto-select an instrument whose row renders disabled — leaving the user
      // unable to deselect it and Pay submitting a bank id that dead-ends at
      // PENDING_SETTLEMENT_UNSUPPORTED. Disabling the row is not enough on its own.
      const selectable = savedPaymentMethods.filter(
        pm => !UNSELECTABLE_METHOD_TYPES.includes(pm.methodType ?? 'card')
      );
      const defaultMethod = selectable.find(pm => pm.isDefault);
      const methodToSelect = defaultMethod || selectable[0];

      if (methodToSelect) {
        console.log('[SubscriptionReviewStep] Auto-selecting payment method:', methodToSelect.id, 'isDefault:', methodToSelect.isDefault);
        setSelectedPaymentMethodId(methodToSelect.id.toString());
        setPaymentMethodId(methodToSelect.id, paymentProvider as PaymentProvider);
      }
    }
  }, [isAuthenticated, savedPaymentMethods, selectedPaymentMethodId, showNewPaymentForm, paymentProvider, setPaymentMethodId]);

  // Redirect if no subscription selected
  if (!selectedSubscription) {
    return null;
  }

  // Handle "Save Payment Method" click
  const handleAddPaymentMethod = async () => {
    try {
      setIsProcessing(true);

      // Validate club is loaded
      if (!club?.id) {
        butterToast.error('Club information not loaded. Please refresh the page.');
        setIsProcessing(false);
        return;
      }

      // Call /users/incomplete to create or find user with Supabase account
      const result = await userService.createOrFindIncompleteUser({
        email: state.email.toLowerCase().trim(),
        clubId: club.id.toString(),
        source: 'subscription',
      });

      console.log('[SubscriptionReviewStep] Incomplete user result:', result);

      // Show success message if provided by API
      if (result.message) {
        console.log('[SubscriptionReviewStep] API message:', result.message);
      }

      // Store user in context (access via result.payload)
      setGuestUser(result.payload.user);

      /**
       * `pending` bundled with `incomplete` — see the full rationale at
       * PaymentDetailsStep.tsx. Short version: neither has a user-chosen password, so
       * both continue to CompleteAccountForm rather than the sign-in modal, and the
       * `pending` half depends on the TEMPORARY G23 window in the API.
       * @see /apps/api/src/services/auth/docs/KNOWN_GAPS.md G23, G8
       */
      const isIncompleteUser = needsAccountSetup(result.payload.user);

      if (result.payload.isExisting && !isIncompleteUser) {
        // Existing user with password - show sign-in modal
        console.log('[SubscriptionReviewStep] Existing user with active status, showing sign-in modal');
        setPaymentStep('sign-in');
      } else {
        // New user OR incomplete user - proceed to payment
        console.log('[SubscriptionReviewStep] New user or incomplete user, proceeding to payment step');
        setPaymentStep('payment');
      }
    } catch (err) {
      console.error('[SubscriptionReviewStep] Failed to check user:', err);
      butterToast.error(
        err instanceof Error
          ? err.message
          : 'Failed to process request. Please try again.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle successful sign-in
  const handleSignInSuccess = () => {
    console.log('[SubscriptionReviewStep] Sign-in successful');
    setPaymentStep('payment');
  };

  // Handle selecting an existing saved payment method
  const handleSelectPaymentMethod = (pmId: string) => {
    console.log('[SubscriptionReviewStep] Selected existing payment method:', pmId);

    addBreadcrumb(
      'payment',
      'User selected saved payment method for subscription',
      {
        paymentMethodId: pmId,
        subscriptionType: selectedSubscription.subscriptionType,
      },
      'info'
    );

    setSelectedPaymentMethodId(pmId);

    // Store the payment method ID in context
    setPaymentMethodId(parseInt(pmId, 10), paymentProvider as PaymentProvider);

    // Don't auto-advance for authenticated users - they need to click Pay button
  };

  // Handle "Add new payment method" click
  const handleAddNewPaymentMethod = () => {
    console.log('[SubscriptionReviewStep] User wants to add new payment method');

    // Ensure guestUser is set from authenticated user for payment processing
    if (isAuthenticated && authUser && !guestUser) {
      console.log('[SubscriptionReviewStep] Setting guestUser from authUser for new payment method');
      setGuestUser(authUser, true);
    }

    setShowNewPaymentForm(true);
    setSelectedPaymentMethodId(null);
    setPaymentMethodId(null, null);
  };

  // Handle payment method saved
  const handlePaymentSuccess = async (providerToken: string) => {
    console.log('[SubscriptionReviewStep] Payment token received from provider:', providerToken);
    console.log('[SubscriptionReviewStep] Payment provider:', paymentProvider);

    try {
      setIsProcessing(true);

      // Ensure guestUser is set from authenticated user if not already set
      if (isAuthenticated && authUser && !guestUser) {
        console.log('[SubscriptionReviewStep] Setting guestUser from authUser in handlePaymentSuccess');
        setGuestUser(authUser, true);
      }

      // Get user ID - prefer authenticated user, fallback to guest user
      const userId = authUser?.id || (guestUser as any)?.id; // eslint-disable-line @typescript-eslint/no-explicit-any

      // Validate required data
      if (!userId) {
        throw new Error('User ID not found. Please restart the purchase process.');
      }
      if (!club?.id) {
        throw new Error('Club ID not found. Please refresh the page.');
      }
      if (!paymentProvider) {
        throw new Error('Payment provider not detected.');
      }

      console.log('[SubscriptionReviewStep] Saving payment method to database...');

      // Persist the card (Stripe confirm-setup-intent or Square token) and get
      // its database id — the shared primitive handles the retry-on-confirm-failure.
      const databaseId = await saveCard(providerToken);

      console.log('[SubscriptionReviewStep] Payment method saved with database ID:', databaseId);

      // Store DATABASE ID in context
      setPaymentMethodId(databaseId, paymentProvider as PaymentProvider);

      // For authenticated users: reset form state and select the new payment method
      // This will show the saved payment methods list with the new one selected
      if (isAuthenticated) {
        console.log('[SubscriptionReviewStep] Authenticated user - selecting newly saved payment method');
        setShowNewPaymentForm(false);
        setSelectedPaymentMethodId(databaseId.toString());
      }

      // Set flag to trigger auto-advance in useEffect
      setPaymentSaved(true);

      console.log('[SubscriptionReviewStep] Payment saved, waiting for state update...');
    } catch (err) {
      console.error('[SubscriptionReviewStep] Failed to save payment method:', err);
      // Broad client-side capture: client-state guard, post-`/confirm`
      // `!databaseId` response anomaly, or a re-thrown persist failure. Provider
      // SDK failures were already captured inside the card form.
      reportPaymentClientError('save_card', err, {
        kind: 'client_flow',
        surface: 'web_subscription',
        extra: { provider: paymentProvider },
      });
      butterToast.error(
        err instanceof Error
          ? err.message
          : 'Failed to save payment method. Please try again.'
      );
      setIsProcessing(false);
      setPaymentSaved(false);
      setPaymentMethodId(null, null);
    }
  };

  // Handle back to subscription selection
  const handleBackToSelection = () => {
    // Remove the ?plan= param so the selection step shows the full list (not pre-selected)
    setParams({ [QueryParams.PLAN]: null });
    goToPreviousStep();
  };

  // Handle Pay button click for authenticated users
  const handlePayNow = async () => {
    // Pre-mutate client guards — parse everything the mutation needs while the
    // null-checks narrow. A failure here is a client_flow issue, captured once.
    let userId: number;
    let subscriptionId: number;
    let clubId: number;
    let paymentMethodId: number | undefined;
    let isMembership: boolean;
    // A guard failure below overwrites the banner — it must not keep the kind of
    // an earlier holding refusal.
    setRefusalKind(null);
    try {
      if (!authUser) {
        throw new Error('User not found. Please sign in again.');
      }
      if (!selectedSubscription) {
        throw new Error('No subscription selected. Please start over.');
      }
      if (!noCardNeeded && !selectedPaymentMethodId) {
        throw new Error('Please select a payment method.');
      }
      if (!club) {
        throw new Error('Club information not loaded. Please refresh the page.');
      }
      if (priceUnknown || !quoteQuery.data) {
        throw new Error('The price has not been confirmed yet. Please wait a moment.');
      }

      userId = typeof authUser.id === 'string' ? parseInt(authUser.id, 10) : authUser.id;
      if (isNaN(userId)) {
        throw new Error('Invalid user ID. Please try logging in again.');
      }

      subscriptionId = typeof selectedSubscription.id === 'number'
        ? selectedSubscription.id
        : parseInt(String(selectedSubscription.id), 10);
      if (isNaN(subscriptionId)) {
        throw new Error('Invalid subscription ID');
      }

      clubId = club.id;
      // No card when nothing is owed or credit covers it — the server settles from credit.
      paymentMethodId = noCardNeeded || !selectedPaymentMethodId ? undefined : parseInt(selectedPaymentMethodId, 10);
      isMembership = selectedSubscription.subscriptionType === 'membership';
    } catch (err) {
      console.error('[SubscriptionReviewStep] Pay-now validation failed:', err);
      reportPaymentClientError('pay_now_guards', err, {
        kind: 'client_flow',
        level: 'warning',
        surface: 'web_subscription',
      });
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to create subscription. Please try again.'
      );
      return;
    }

    // Execute — every API outcome resolves as an envelope and is owned by the
    // mutation's onSuccess/onFailure handlers. mutateAsync only rejects on a
    // programmer error (onError reports it); the .catch just prevents that
    // rare rejection from surfacing unhandled.
    setIsProcessing(true);
    setError(null);
    setPromoDropped(false);
    setRefusalKind(null);
    setRefusedRecipient(null);
    if (isMembership) {
      await createMembership
        .createUserMembership({
          userId,
          membershipId: subscriptionId,
          ...(paymentMethodId !== undefined && { paymentMethodId }),
          ...(promoCodeForPurchase && { promoCode: promoCodeForPurchase }),
        })
        .catch(() => {});
    } else {
      await createPass
        .createUserPass({
          userId,
          passId: subscriptionId,
          clubId,
          ...(paymentMethodId !== undefined && { paymentMethodId }),
          ...(promoCodeForPurchase && { promoCode: promoCodeForPurchase }),
          // Empty for a single pass — that request stays exactly what it was.
          ...giftOrderFields(recipients, quantity, giftAllEffective),
        })
        .catch(() => {});
    }
  };

  // Club data is guaranteed loaded by ClubGate before this step renders.
  // Minimal defensive guard — no loading UI, since "still loading" is not
  // reachable here.
  if (!club) {
    return null;
  }

  // Note: selectedSubscription guard already exists at line ~381
  // No need for redundant check here

  return (
    <div className="">
      {/* Back to Subscription Selection Link */}
      <button
        onClick={handleBackToSelection}
        data-testid="subscription-review-back-btn"
        className="inline-flex items-center gap-2 text-sm text-fg-quaternary text-interactive:text-fg-tertiary transition-colors"
      >
        <ArrowIcon className="h-4 w-4 rotate-180" />
        Back to Subscription Selection
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-24 mt-10">

        {/* Left Column: Gradient card + Features + Price Summary */}
        <div className="flex flex-col gap-8">
          <MembershipCardGradient
            membership={{
              name: selectedSubscription.name,
              description: selectedSubscription.description,
              price: String(parseFloat(selectedSubscription.price.replace(/[^\d.]/g, '')) || 0),
              billingFrequency: selectedSubscription.billingFrequency ?? null,
              color: selectedSubscription.color,
            }}
          />

          {showQuantity && (
            <>
              <QuantityStepper
                quantity={quantity}
                max={maxQuantity}
                onChange={setQuantity}
                disabled={isProcessing}
              />
              {!giftStatusPending && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2.5">
                    <Checkbox
                      id="subscription-gift-all"
                      checked={giftAllEffective}
                      disabled={isProcessing || giftLocked}
                      onCheckedChange={(checked) => setGiftAll(checked === true)}
                      // The primitive paints Butter purple; every club-facing
                      // surface is themed. Same override the mobile checkout uses.
                      className="mt-0.5 focus-visible:ring-theme-600 data-[state=checked]:border-theme-600 data-[state=checked]:bg-theme-600"
                      data-testid="subscription-gift-all"
                    />
                    <label htmlFor="subscription-gift-all" className="text-sm text-fg-primary">
                      This is a gift
                    </label>
                  </div>
                  {/* A fact about their plan, not advice: it names the kind the
                      server would refuse the order for — the pass, or the
                      membership (the team's, a queued one). `data-locked` stays
                      for the specs that read it. */}
                  {giftLocked && (
                    <p
                      className="text-sm text-fg-tertiary"
                      data-testid="subscription-gift-all-locked"
                      data-locked={giftLocked}
                      data-locked-by={giftLockedBy ?? undefined}
                    >
                      You already hold {giftLockedByName ? `the ${giftLockedByName}` : `a ${giftLockedBy}`}. You
                      can still gift to share the fun — or contact the club to change your plan.
                    </p>
                  )}
                </div>
              )}
              <GiftRecipientsField
                count={recipientsNeeded(quantity, giftAllEffective)}
                recipients={recipients}
                clubId={club.id}
                canCheck={isAuthenticated}
                disabled={isProcessing}
                refusedIndex={refusedRecipient}
                giftAll={giftAllEffective}
                message={promotion?.applied && promotion.giftingMessage ? promotion.giftingMessage : giftAllEffective ? GIFT_ALL_MESSAGE : DEFAULT_GIFTING_MESSAGE}
                onChange={(index, patch) => {
                  if (index === refusedRecipient) setRefusedRecipient(null);
                  setRecipient(index, patch);
                }}
              />
            </>
          )}

          {/* What this membership/pass includes — authored key benefits or derived fallback */}
          {benefits.length > 0 && (
            <div className="rounded-3xl border border-gray-200 bg-butter-gray-50 p-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                What this {selectedSubscription.subscriptionType} includes
              </h3>
              <KeyBenefitsList benefits={benefits} size="md" tone="muted" clampDescription={2} />
            </div>
          )}

          <PromoCodeField promotion={promotion} />

          {promoGiftingNote(promotion) && recipientsNeeded(quantity, giftAllEffective) < 1 && (
            <p className="text-sm text-fg-tertiary" data-testid="review-promo-gifting-message">
              {promoGiftingNote(promotion)}
            </p>
          )}

          {promotion && !promotion.applied && (
            <PromoNotice variant="refused" reason={promotion.reason} testId="review-promo-refused">
              {promotion.message ?? 'This promo code could not be applied.'}{' '}
              {quantityFix ? (
                <button
                  type="button"
                  className="font-semibold underline"
                  data-testid="review-promo-fix-quantity"
                  onClick={() => setQuantity(quantityFix)}
                >
                  Change to {quantityFix} {quantityFix === 1 ? 'pass' : 'passes'}
                </button>
              ) : (
                'You can still complete your purchase at the regular price.'
              )}
            </PromoNotice>
          )}

          {quoteUnavailable && (
            <QuoteFailedBanner
              onRetry={() => void quoteQuery.refetch()}
              testId="review-quote-failed"
            />
          )}

          {/* Price Summary — or the server's refusal, which takes priority. */}
          {quoteRefused && (quoteQuery.data?.errors.length ?? 0) > 0 ? (
            <QuoteRefusedPanel errors={quoteQuery.data?.errors ?? []} testId="review-quote-refused" />
          ) : (
            <PriceSummaryCard
              pending={priceUnknown}
              lineItems={displayLineItems}
              showSubtotal
              subtotal={subtotal}
              tax={tax}
              total={total}
              taxRatePercent={taxRate}
              processingFee={processingFee}
              processingFeeRate={processingFeeRate}
              subscriptionDiscount={discountTotal > 0 ? discountTotal : undefined}
              discountLabel={promotion?.applied ? promotion.label : undefined}
              credit={credit}
            />
          )}
        </div>

        {/* Right Column: Billing Details + Payment */}
        <div className="flex flex-col gap-8">
          <BookingDetailsCard
            title="Billing Details"
            startDate={billingDetails.startDate}
            billingCycle={billingDetails.billingCycle}
            nextBillingDate={billingDetails.nextBillingDate}
            subscriptionType={selectedSubscription.subscriptionType}
          />

          {/* Error Message */}
          {error && (
            <div
              className="rounded-lg border-2 border-red-200 bg-red-50 p-4 mb-4"
              data-testid="review-error"
              data-kind={promoDropped ? 'promotion_rejected' : refusalKind ?? 'error'}
            >
              <p className="text-sm text-red-800">⚠️ {error}</p>
            </div>
          )}

          {/* Payment Flow - Conditional Rendering Based on Step */}

          {/* STEP 1: Email Entry */}
          {paymentStep === 'email' && (
            <EmailPaymentCard
              email={state.email}
              onEmailChange={setEmail}
              onAddPaymentMethod={handleAddPaymentMethod}
              isProcessing={isProcessing}
            />
          )}

          {/* STEP 2: Payment Method Capture */}
          {paymentStep === 'payment' && (
            <div className="rounded-3.5xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Payment Method
              </h3>

              {/* Only show email input for unauthenticated users */}
              {!isAuthenticated && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={state.email}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                  />
                </div>
              )}

              {/* Nothing for a card to pay — a free plan, or the member's account credit
                  covers it (credits X6): no card UI, just confirm. */}
              {noCardNeeded ? (
                <div className="space-y-4">
                  <div
                    className="flex items-start gap-3 rounded-2xl border border-theme-600 bg-theme-50 px-4 py-3"
                    data-testid="subscription-review-no-card-needed"
                  >
                    <CheckmarkIcon className="mt-0.5 size-5 shrink-0 text-green-600" aria-hidden="true" />
                    <p className="text-base font-medium text-fg-primary">
                      {creditCovered
                        ? 'Paid in full with account credit — no card needed'
                        : 'No payment needed'}
                    </p>
                  </div>
                  <button
                    onClick={handlePayNow}
                    disabled={isProcessing || priceUnknown || !recipientsComplete}
                    data-testid="subscription-review-pay-btn"
                    className="w-full px-6 py-3 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing
                      ? 'Processing...'
                      : priceUnknown
                        ? 'Waiting for price…'
                        : !recipientsComplete
                          ? 'Add recipient details to continue'
                          : 'Confirm Purchase'}
                  </button>
                </div>
              ) : isAuthenticated && savedPaymentMethods.length > 0 && !showNewPaymentForm ? (
                <>
                  <PaymentMethodSelector
                    // Bank methods can't be charged until the ACH settlement flow
                    // lands — selecting one dead-ends at the confirm button with
                    // PENDING_SETTLEMENT_UNSUPPORTED. Remove in Phase 3.
                    unselectableMethodTypes={UNSELECTABLE_METHOD_TYPES}
                    unselectableReason="Bank accounts can't be used for payment yet"
                    paymentMethods={savedPaymentMethods}
                    selectedPaymentMethodId={selectedPaymentMethodId}
                    onSelect={handleSelectPaymentMethod}
                    onAddNew={handleAddNewPaymentMethod}
                    isProcessing={isProcessing}
                  />

                  {/* Pay Button for Authenticated Users */}
                  {selectedPaymentMethodId && (
                    <div className="mt-6 space-y-4">
                      {/* Pay Button */}
                      <button
                        onClick={handlePayNow}
                        disabled={isProcessing || priceUnknown || !recipientsComplete}
                        data-testid="subscription-review-pay-btn"
                        className="w-full px-6 py-3 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {/* Name what is being waited on, the way `priceUnknown`
                            already does. A buyer who holds a membership now meets
                            the recipients gate BY DEFAULT at quantity 1, without
                            having asked to gift anything — and the explanation for
                            it sits in the other column. A button reading
                            "Pay $101.25" while greyed out is the worst of both. */}
                        {isProcessing
                          ? 'Processing...'
                          : priceUnknown
                            ? 'Waiting for price…'
                            : !recipientsComplete
                              ? 'Add recipient details to continue'
                              : `Pay $${payNowLabel}`}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                /* Show payment form for new users or when "add new" is clicked */
                <>
                  <CardEntryForm
                    paymentProvider={paymentProvider}
                    stripeSetupIntent={stripeSetupIntent}
                    stripeSetupIntentLoading={stripeSetupIntentLoading}
                    stripeSetupIntentError={stripeSetupIntentError}
                    retryStripeSetupIntent={retryStripeSetupIntent}
                    paymentSettings={effectivePaymentSettings}
                    onCardSuccess={handlePaymentSuccess}
                    errorLabel="SubscriptionReviewStep"
                    // Without these a confirm failure on the highest-traffic surface
                    // reaches Sentry with no user or club to pivot on.
                    defaultEmail={authUser?.email}
                    errorContext={{ userId: cardUserId, clubId: club?.id }}
                  />

                  {/* Back to saved methods button (if applicable) */}
                  {isAuthenticated && savedPaymentMethods.length > 0 && showNewPaymentForm && (
                    <button
                      type="button"
                      onClick={() => setShowNewPaymentForm(false)}
                      className="mt-4 text-sm text-blue-600 hover:text-blue-700"
                    >
                      ← Back to saved payment methods
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Sign-In Modal (shown when existing user detected) */}
      <SignInModal
        isOpen={paymentStep === 'sign-in'}
        onClose={() => setPaymentStep('email')}
        onSuccess={handleSignInSuccess}
        prefillEmail={state.email}
      />
    </div>
  );
}
